import React, { FC } from "react";

import log from "loglevel";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InputAdornment } from "@mui/material";

import { EmailIcon } from "@/assets/icons";
import { TextInput } from "@/components/atoms";
import { userApi } from "@/shared/apis";
import { useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import { EmailFormType, emailValidationSchema } from "../../../shared";
import { StyledIndications } from "../../../styled";

const defaultValues = (email: string) => {
    return { email };
};

type ConfirmEmailProps = {
    officeId: string;
    email: string;
    onResetRequest: (email: string) => void;
};

const ConfirmEmail: FC<ConfirmEmailProps> = ({ email, officeId, onResetRequest }) => {
    const { navToRoot } = useNavigation();
    const { t, i18n } = useTranslation();
    const { mutate: requestResetPassword } = useMutation(userApi.resetPasswordInit);

    const { handleSubmit, control } = useForm<EmailFormType>({
        defaultValues: defaultValues(email),
        resolver: yupResolver(emailValidationSchema),
    });

    const handleRequestResetPassword = (data: EmailFormType) => {
        try {
            log.info("request reset password");
            requestResetPassword({
                email: data.email,
                language: i18n.language,
                officeId,
            });
            tagManager.pushChangePasswordSentEvent();
            log.info("request reset password success");
            onResetRequest(data.email);
        } catch (e) {
            log.warn("request reset password failed");
            throw e;
        }
    };

    return (
        <form onSubmit={handleSubmit(handleRequestResetPassword)}>
            <div className="title">{t("ResetPassword.ConfirmEmail.Title")}</div>
            <StyledIndications>
                <p> {t("ResetPassword.ConfirmEmail.Message")} </p>
            </StyledIndications>
            <TextInput
                name="email"
                autoComplete="off"
                control={control}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <EmailIcon />
                        </InputAdornment>
                    ),
                }}
            />
            <div className="form-bottom-container">
                <Button
                    data-testid="button_back_to_login"
                    id="btn_backToLogin"
                    fullWidth
                    variant="outlined"
                    onClick={() => navToRoot()}
                >
                    {t("ResetPassword.ConfirmEmail.BackToLogin")}
                </Button>
                <Button data-testid="button_confirm" fullWidth className="mt16" type="submit">
                    {t("Common.Confirm")}
                </Button>
            </div>
        </form>
    );
};

export default ConfirmEmail;
